import React, { useState, useEffect } from "react";
import axios from "axios";
import MobileStepper from "@mui/material/MobileStepper";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import "./styleLogin.css";
// import StripeContainer from "./StripeContainer";
import logo from "../../logos/MSALogoFinal.png";

function Register() {
  // ----------------------------------
  // State
  // ----------------------------------
  const [activeStep, setActiveStep] = useState(0);
  const [selectedModules, setSelectedModules] = useState({
    mapSystem: false,
    callModule: false
  });
  const [clientInfo, setClientInfo] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [isErrorMessage, setIsErrorMessage] = useState(null);
  
  // Multi-client registration state
  const [showEmailOptionsModal, setShowEmailOptionsModal] = useState(false);
  const [existingClients, setExistingClients] = useState([]);
  const [registrationMode, setRegistrationMode] = useState('create'); // 'create' or 'join'
  const [selectedExistingClient, setSelectedExistingClient] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Payment
  const [payingInvoice, setPayingInvoice] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentResult, setPaymentResult] = useState("");

  // Pricing constants
  const PRICING = {
    mapSystem: {
      monthly: 84,
      yearly: 1000,
      description: "Interactive mapping and asset management"
    },
    callModule: {
      monthly: 84,
      yearly: 1000,
      description: "Call handling and management system"
    },
    // Bundle discount pricing
    bundle: {
      monthly: 150,
      yearly: 1800,
      description: "Both modules with special discount"
    }
  };

  // Helper functions for pricing calculations
  const calculateTotals = () => {
    let monthlyTotal, yearlyTotal;
    
    // Check if both modules are selected for bundle pricing
    if (selectedModules.mapSystem && selectedModules.callModule) {
      monthlyTotal = PRICING.bundle.monthly;
      yearlyTotal = PRICING.bundle.yearly;
    } else {
      // Individual pricing
      monthlyTotal = (selectedModules.mapSystem ? PRICING.mapSystem.monthly : 0) + 
                    (selectedModules.callModule ? PRICING.callModule.monthly : 0);
      yearlyTotal = (selectedModules.mapSystem ? PRICING.mapSystem.yearly : 0) + 
                   (selectedModules.callModule ? PRICING.callModule.yearly : 0);
    }
    
    const monthlyYearlyTotal = (selectedModules.mapSystem ? PRICING.mapSystem.monthly : 0) + 
                              (selectedModules.callModule ? PRICING.callModule.monthly : 0);
    const yearlySavings = monthlyYearlyTotal * 12 - yearlyTotal;
    
    return { monthlyTotal, yearlyTotal, monthlyYearlyTotal, yearlySavings };
  };

  const getSelectedModulesCount = () => {
    return Object.values(selectedModules).filter(Boolean).length;
  };

  // Demo - Commented out for now
  // const [askForDemoCode, setAskForDemoCode] = useState(false);
  // const [demoCode, setDemoCode] = useState("");
  // const [demoCodeError, setDemoCodeError] = useState("");

  // ----------------------------------
  // Lifecycle
  // ----------------------------------
  useEffect(() => {
    const checkLogin = async () => {
      try {
        const { data } = await axios.get("/api/isLoggedIn");
        if (data.loggedIn) {
          window.location = "/";
        }
      } catch (err) {
        console.error("Error checking login status:", err);
      }
    };
    checkLogin();
  }, []);

  // ----------------------------------
  // Handlers
  // ----------------------------------
  // 1) For user info (first name, last name, etc.)
  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  // 2) For client info
  const handleClientInfoChange = (e) => {
    const { name, value } = e.target;
    setClientInfo((prev) => ({ ...prev, [name]: value }));
  };

  // 3) Confirm password validation
  const handleConfirmPassword = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  // 4) Next / Back
  const handleNext = async (e) => {
    e.preventDefault();
    if (activeStep === 0) {
      // Validate required fields
      if (!userInfo.first || !userInfo.last || !userInfo.title || !userInfo.email || !userInfo.password || !userInfo.confirm_password) {
        setIsErrorMessage("Please fill in all required fields.");
        return;
      }
      
      // Validate password match
      if (userInfo.password !== userInfo.confirm_password) {
        setIsErrorMessage("Passwords do not match.");
        return;
      }
      
      // Validate password strength (minimum 6 characters)
      if (userInfo.password.length < 6) {
        setIsErrorMessage("Password must be at least 6 characters long.");
        return;
      }
      
      // check for email
      const loginObject = { Email: userInfo.email };
      try {
        const { data } = await axios.post("/api/userEmailCheck", loginObject);
        if (data && data.exists) {
          // Email exists, show options modal
          setExistingClients(data.clients || []);
          setShowEmailOptionsModal(true);
        } else {
          // Email doesn't exist, proceed with new registration
          setRegistrationMode('create');
          document.getElementById("registerForm").reset();
          setActiveStep((prev) => prev + 1);
          setIsErrorMessage(null);
        }
      } catch (err) {
        console.error(err);
        setIsErrorMessage("Error checking email.");
      }
    } else {
      document.getElementById("registerForm").reset();
      setActiveStep((prev) => prev + 1);
      setIsErrorMessage(null);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    document.getElementById("registerForm").reset();
    setActiveStep((prev) => prev - 1);
  };

  // 5) Module selection
  const handleModuleSelection = (module) => {
    setSelectedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
    
    // Update clientInfo.plan for legacy system integration
    // 1 = map system only, 2 = call module only, 3 = both
    const newSelectedModules = {
      ...selectedModules,
      [module]: !selectedModules[module]
    };
    
    let planNumber;
    if (newSelectedModules.mapSystem && newSelectedModules.callModule) {
      planNumber = 3; // both
    } else if (newSelectedModules.mapSystem) {
      planNumber = 1; // map only
    } else if (newSelectedModules.callModule) {
      planNumber = 2; // call only
    } else {
      planNumber = 0; // none selected
    }
    
    setClientInfo(prev => ({ ...prev, plan: planNumber }));
  };

  // 6) Demo code - Commented out for now
  /*
  const handleCheckDemoCode = async (event) => {
    event.preventDefault();
    if (!demoCode) {
      setDemoCodeError("Please enter a demo code.");
      setTimeout(() => {
        setDemoCodeError("");
        setAskForDemoCode(false);
      }, 2000);
      return;
    }
    try {
      const checkObject = {
        demoCode,
        userInfo: { ...userInfo, ...clientInfo },
        selectedModules
      };
      const { data } = await axios.post("/userRegister/check-demo-code", checkObject);
      if (data.success) {
        // success
        setAskForDemoCode(false);
        setDemoCodeError("");
        
        // proceed to "pay" for the demo
        const paymentObject = {
          price: "price_1PlWWlRsqwelbBw0knrR97lH",
          currency: "usd",
          email: userInfo.email,
          name: clientInfo.clientName,
        };
        const { data: paymentResultData } = await axios.post("/userRegister/demo-payment", paymentObject);
        if (paymentResultData.message === "Payment succeeded") {
          // create user
          setPaymentResult("Payment successful!");
          const loginObject = {
            email: userInfo.email,
            password: userInfo.password,
            clientInfo, // This now includes clientInfo.plan for legacy integration
            userInfo,
            selectedModules,
            subscription: paymentResultData.subscription,
          };
          try {
            await axios.post("/userRegister/adduser", loginObject);
            window.location = "/login";
          } catch (err) {
            console.error(err);
            setPaymentResult("User add failed");
          }
        } else {
          console.error(paymentResultData.message);
          setPaymentResult(paymentResultData.message);
        }
      } else {
        setDemoCodeError("Invalid demo code.");
        setTimeout(() => {
          setDemoCodeError("");
          setAskForDemoCode(false);
        }, 2000);
      }
    } catch (err) {
      console.error("Error checking demo code:", err);
      setDemoCodeError("Error checking demo code.");
      setTimeout(() => {
        setDemoCodeError("");
        setAskForDemoCode(false);
      }, 2000);
    }
  };
  */

  // 7) Invoice - Commented out as invoicing will be handled outside the system
  /*
  const handleInvoicePayment = async (e) => {
    e.preventDefault();
    setPayingInvoice(true);
    const invoiceData = {
      email: userInfo.email,
      clientInfo, // This now includes clientInfo.plan for legacy integration
      selectedModules,
      subInfo: { userInfo, clientInfo },
    };
    try {
      const response = await axios.post("/userRegister/create-invoice", invoiceData);
      if (response.data.success) {
        console.log(`Invoice Created! Invoice ID: ${response.data.invoiceId}`);
        alert("Invoice created successfully. Please check your email for payment instructions.");
        window.location = "/login";
      } else {
        console.log("Failed to create invoice.");
      }
    } catch (err) {
      console.error("Invoice error:", err);
      alert("Error creating invoice.");
    } finally {
      setPayingInvoice(false);
    }
  };
  */

  // 7) Handle email options modal
  const handleCreateNewClient = () => {
    setRegistrationMode('create');
    setShowEmailOptionsModal(false);
    document.getElementById("registerForm").reset();
    setActiveStep((prev) => prev + 1);
    setIsErrorMessage(null);
  };

  const handleJoinExistingClient = () => {
    if (!selectedExistingClient) {
      setIsErrorMessage("Please select a client organization to join.");
      return;
    }
    setRegistrationMode('join');
    setShowEmailOptionsModal(false);
    document.getElementById("registerForm").reset();
    setActiveStep((prev) => prev + 1);
    setIsErrorMessage(null);
  };

  const handleCancelEmailOptions = () => {
    setShowEmailOptionsModal(false);
    setSelectedExistingClient('');
    setExistingClients([]);
    setIsErrorMessage(null);
  };

  // 8) Submit registration
  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (registrationMode === 'join') {
      // Join existing client flow
      if (!selectedExistingClient) {
        setIsErrorMessage("Please select a client organization to join.");
        setIsLoading(false);
        return;
      }
      
      try {
        const joinObject = {
          email: userInfo.email,
          password: userInfo.password,
          userInfo,
          selectedClientID: selectedExistingClient
        };
        
        const response = await axios.post("/userRegister/join-client", joinObject);
        
        if (response.status === 200 && response.data.success) {
          console.log("Join successful:", response.data);
          alert("Successfully joined client organization! Welcome to the app!");
          window.location = "/login";
        } else {
          setIsErrorMessage("Failed to join client organization. Please try again.");
        }
      } catch (err) {
        console.error("Join error:", err);
        if (err.response && err.response.data && err.response.data.error) {
          setIsErrorMessage(`Join failed: ${err.response.data.error}`);
        } else {
          setIsErrorMessage("Failed to join client organization. Please try again.");
        }
      }
    } else {
      // Create new client flow
      // Validate that at least one module is selected
      if (!selectedModules.mapSystem && !selectedModules.callModule) {
        setIsErrorMessage("Please select at least one module.");
        setIsLoading(false);
        return;
      }
      
      // Validate that clientInfo.plan is set
      if (!clientInfo.plan || clientInfo.plan === 0) {
        setIsErrorMessage("Please select at least one module.");
        setIsLoading(false);
        return;
      }
      
      try {
        const registrationObject = {
          email: userInfo.email,
          password: userInfo.password,
          userInfo,
          clientInfo, // This includes clientInfo.plan for legacy integration
          subscription: null // No subscription for external payment handling
        };
        
        const response = await axios.post("/userRegister/adduser", registrationObject);
        
        if (response.status === 200) {
          console.log("Registration successful:", response.data);
          alert("Registration submitted successfully! EnJoy the app! We will contant you regarding your interest.");
          window.location = "/login";
        } else {
          setIsErrorMessage("Registration failed. Please try again.");
        }
      } catch (err) {
        console.error("Registration error:", err);
        if (err.response && err.response.data && err.response.data.error) {
          setIsErrorMessage(`Registration failed: ${err.response.data.error.message || err.response.data.error}`);
        } else {
          setIsErrorMessage("Registration failed. Please try again.");
        }
      }
    }
    
    setIsLoading(false);
  };

  // ----------------------------------
  // Render
  // ----------------------------------
  return (
    <div className="loginBackGround" style={{ overflow: "scroll" }}>
      <div className="loginContainer" style={{ paddingTop: "4vh" }}>
        <img
          src={logo}
          alt="logo"
          style={{
            width: "64%",
            height: "auto",
            margin: "auto",
            display: "block",
          }}
        />
        <form className="register" id="registerForm">
          {activeStep === 0 ? (
            <div>
              <h2 className="registerTag">User Info</h2>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="first"
                  placeholder="First Name"
                  value={userInfo.first || ""}
                  onChange={handleUserInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="last"
                  placeholder="Last Name"
                  value={userInfo.last || ""}
                  onChange={handleUserInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="title"
                  placeholder="Job Title"
                  value={userInfo.title || ""}
                  onChange={handleUserInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="email"
                  placeholder="Email"
                  value={userInfo.email || ""}
                  onChange={handleUserInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-lock" />
                <input
                  type="password"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="password"
                  placeholder="Password"
                  value={userInfo.password || ""}
                  onChange={handleUserInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-lock" />
                <input
                  type="password"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="confirm_password"
                  placeholder="Confirm Password"
                  value={userInfo.confirm_password || ""}
                  onChange={handleConfirmPassword}
                />
              </div>
            </div>
          ) : activeStep === 1 ? (
            <div>
              <h2 className="registerTag">Client Info</h2>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="clientName"
                  placeholder="Client Name"
                  value={clientInfo.clientName || ""}
                  onChange={handleClientInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="ClientDescription"
                  placeholder="Description"
                  value={clientInfo.ClientDescription || ""}
                  onChange={handleClientInfoChange}
                />
              </div>

              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="Address"
                  placeholder="Address"
                  value={clientInfo.Address || ""}
                  onChange={handleClientInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="city"
                  placeholder="City"
                  value={clientInfo.city || ""}
                  onChange={handleClientInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="State"
                  placeholder="State"
                  value={clientInfo.State || ""}
                  onChange={handleClientInfoChange}
                />
              </div>
              <div className="register__field">
                <i className="login__icon fas fa-user" />
                <input
                  type="text"
                  className="login__input"
                  style={{ boxShadow: "0px 2px 11px #000000b0" }}
                  required
                  name="ZipCode"
                  placeholder="ZipCode"
                  value={clientInfo.ZipCode || ""}
                  onChange={handleClientInfoChange}
                />
              </div>
            </div>
          ) : activeStep === 2 ? (
            <div>
              <h2 className="registerTag">Select Modules</h2>
              <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
                Choose which modules you'd like to include in your subscription
              </p>
              
              {/* Demo functionality - Commented out for now
              {!askForDemoCode ? (
                <button
                  className="button login__submit paymentButton"
                  style={{ boxShadow: "0px 2px 11px #000000b0", marginBottom: "15px" }}
                  onClick={(e) => {
                    e.preventDefault();
                    setAskForDemoCode(true);
                  }}
                >
                  <div>
                    <span className="button__text">
                      Demo - <span className="paymentDescription">Free for 14 days</span>
                    </span>
                  </div>
                  <span className="price">Free</span>
                </button>
              ) : (
                <div className="register__field" style={{ marginBottom: "15px" }}>
                  <input
                    type="text"
                    className="form-input"
                    style={{ boxShadow: "0px 2px 11px #000000b0", flex: "1" }}
                    placeholder="Enter Demo Code"
                    value={demoCode}
                    onChange={(e) => setDemoCode(e.target.value)}
                  />
                  {demoCodeError && (
                    <div style={{ color: "red", fontSize: "12px" }}>{demoCodeError}</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-evenly", paddingTop: "10px" }}>
                    <button
                      className="submit-button"
                      style={{ marginLeft: "10px", padding: "8px 15px", width: "auto" }}
                      onClick={(evt) => handleCheckDemoCode(evt)}
                    >
                      Submit
                    </button>
                    <button
                      className="cancel-button"
                      style={{ marginLeft: "10px", padding: "8px 15px", width: "auto" }}
                      onClick={(evt) => {
                        evt.preventDefault();
                        setDemoCode("");
                        setAskForDemoCode(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              */}

              {/* Module Selection Header */}
              <div style={{ 
                textAlign: "center", 
                marginBottom: "25px",
                padding: "20px",
                backgroundColor: "#e3f2fd",
                borderRadius: "12px",
                border: "1px solid #bbdefb"
              }}>
                <h3 style={{ 
                  margin: "0 0 10px 0", 
                  color: "#1565c0", 
                  fontSize: "1.4rem",
                  fontWeight: "600"
                }}>
                  Choose Your Modules
                </h3>
                                 <p style={{ 
                   margin: "0", 
                   color: "#1976d2", 
                   fontSize: "1rem",
                   lineHeight: "1.5"
                 }}>
                   Select the modules you need for your city services operations. 
                   <br />
                   <strong>Save money with yearly billing!</strong>
                   <br />
                   <span style={{ color: "#f39c12", fontWeight: "600" }}>
                     🎯 Get both modules for special bundle pricing!
                   </span>
                 </p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {/* Map System Module */}
                <button
                  className={`button login__submit paymentButton ${selectedModules.mapSystem ? 'selected' : ''}`}
                  style={{ 
                    boxShadow: selectedModules.mapSystem ? "0 8px 25px rgba(52, 152, 219, 0.3)" : "0 4px 15px rgba(0,0,0,0.1)",
                    backgroundColor: selectedModules.mapSystem ? "#3498db" : "white",
                    border: selectedModules.mapSystem ? "2px solid #2980b9" : "2px solid #e9ecef",
                    borderRadius: "12px",
                    padding: "20px",
                    transition: "all 0.3s ease",
                    transform: selectedModules.mapSystem ? "translateY(-2px)" : "none"
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleModuleSelection('mapSystem');
                  }}
                >
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    width: "100%"
                  }}>
                    <div style={{ textAlign: "left", flex: "1" }}>
                      <div style={{ 
                        fontSize: "1.3rem", 
                        fontWeight: "700", 
                        color: selectedModules.mapSystem ? "white" : "#2c3e50",
                        marginBottom: "8px"
                      }}>
                        🗺️ Map System
                      </div>
                      <div style={{ 
                        fontSize: "0.95rem", 
                        color: selectedModules.mapSystem ? "rgba(255,255,255,0.9)" : "#6c757d",
                        lineHeight: "1.4"
                      }}>
                        Interactive mapping and asset management
                      </div>
                    </div>
                    <div style={{ 
                      textAlign: "right",
                      color: selectedModules.mapSystem ? "white" : "#2c3e50"
                    }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: "600" }}>
                        ${PRICING.mapSystem.monthly}/month
                      </div>
                      <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>
                        or ${PRICING.mapSystem.yearly}/year
                      </div>
                    </div>
                  </div>
                </button>

                {/* Call Module */}
                <button
                  className={`button login__submit paymentButton ${selectedModules.callModule ? 'selected' : ''}`}
                  style={{ 
                    boxShadow: selectedModules.callModule ? "0 8px 25px rgba(46, 204, 113, 0.3)" : "0 4px 15px rgba(0,0,0,0.1)",
                    backgroundColor: selectedModules.callModule ? "#2ecc71" : "white",
                    border: selectedModules.callModule ? "2px solid #27ae60" : "2px solid #e9ecef",
                    borderRadius: "12px",
                    padding: "20px",
                    transition: "all 0.3s ease",
                    transform: selectedModules.callModule ? "translateY(-2px)" : "none"
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleModuleSelection('callModule');
                  }}
                >
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    width: "100%"
                  }}>
                    <div style={{ textAlign: "left", flex: "1" }}>
                      <div style={{ 
                        fontSize: "1.3rem", 
                        fontWeight: "700", 
                        color: selectedModules.callModule ? "white" : "#2c3e50",
                        marginBottom: "8px"
                      }}>
                        📞 Call Module
                      </div>
                      <div style={{ 
                        fontSize: "0.95rem", 
                        color: selectedModules.callModule ? "rgba(255,255,255,0.9)" : "#6c757d",
                        lineHeight: "1.4"
                      }}>
                        Call handling and management system
                      </div>
                    </div>
                    <div style={{ 
                      textAlign: "right",
                      color: selectedModules.callModule ? "white" : "#2c3e50"
                    }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: "600" }}>
                        ${PRICING.callModule.monthly}/month
                      </div>
                      <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>
                        or ${PRICING.callModule.yearly}/year
                      </div>
                    </div>
                  </div>
                </button>

                                 {/* Bundle Discount Banner */}
                 {selectedModules.mapSystem && selectedModules.callModule && (
                   <div style={{ 
                     marginTop: "20px",
                     padding: "20px",
                     backgroundColor: "#fff3cd",
                     borderRadius: "12px",
                     border: "2px solid #ffeaa7",
                     boxShadow: "0 4px 12px rgba(255, 193, 7, 0.2)",
                     textAlign: "center"
                   }}>
                     <div style={{ 
                       fontSize: "1.2rem", 
                       fontWeight: "700", 
                       color: "#856404",
                       marginBottom: "8px"
                     }}>
                       🎉 Bundle Discount Applied!
                     </div>
                     <div style={{ 
                       fontSize: "1rem", 
                       color: "#856404",
                       lineHeight: "1.4"
                     }}>
                       You're saving <strong>${(PRICING.mapSystem.monthly + PRICING.callModule.monthly) * 12 - PRICING.bundle.yearly}</strong> per year 
                       by choosing both modules together!
                     </div>
                   </div>
                 )}

                 {/* Pricing Summary */}
                 {getSelectedModulesCount() > 0 && (
                   <div style={{ 
                     marginTop: "20px",
                     padding: "20px",
                     backgroundColor: "#f8f9fa",
                     borderRadius: "12px",
                     border: "2px solid #e9ecef",
                     boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                   }}>
                    <h3 style={{ 
                      textAlign: "center", 
                      marginBottom: "20px", 
                      color: "#2c3e50",
                      fontSize: "1.5rem",
                      fontWeight: "600"
                    }}>
                      Pricing Summary
                    </h3>
                    
                    <div style={{ 
                      display: "grid", 
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                      gap: "15px", 
                      marginBottom: "20px" 
                    }}>
                      {/* Monthly Pricing */}
                      <div style={{ 
                        backgroundColor: "white", 
                        padding: "15px", 
                        borderRadius: "8px", 
                        border: "1px solid #dee2e6",
                        textAlign: "center"
                      }}>
                        <h4 style={{ 
                          margin: "0 0 10px 0", 
                          color: "#6c757d", 
                          fontSize: "1rem",
                          fontWeight: "500"
                        }}>
                          Monthly Total
                        </h4>
                        <div style={{ 
                          fontSize: "2rem", 
                          fontWeight: "700", 
                          color: "#dc3545",
                          marginBottom: "5px"
                        }}>
                          ${calculateTotals().monthlyTotal}
                        </div>
                        <div style={{ 
                          fontSize: "0.9rem", 
                          color: "#6c757d" 
                        }}>
                          per month
                        </div>
                      </div>

                      {/* Yearly Pricing */}
                      <div style={{ 
                        backgroundColor: "white", 
                        padding: "15px", 
                        borderRadius: "8px", 
                        border: "1px solid #dee2e6",
                        textAlign: "center"
                      }}>
                        <h4 style={{ 
                          margin: "0 0 10px 0", 
                          color: "#6c757d", 
                          fontSize: "1rem",
                          fontWeight: "500"
                        }}>
                          Yearly Total
                        </h4>
                        <div style={{ 
                          fontSize: "2rem", 
                          fontWeight: "700", 
                          color: "#28a745",
                          marginBottom: "5px"
                        }}>
                          ${calculateTotals().yearlyTotal}
                        </div>
                        <div style={{ 
                          fontSize: "0.9rem", 
                          color: "#6c757d" 
                        }}>
                          per year
                        </div>
                      </div>

                                           {/* Savings */}
                     {calculateTotals().yearlySavings > 0 && (
                       <div style={{ 
                         backgroundColor: "#d4edda", 
                         padding: "15px", 
                         borderRadius: "8px", 
                         border: "1px solid #c3e6cb",
                         textAlign: "center"
                       }}>
                         <h4 style={{ 
                           margin: "0 0 10px 0", 
                           color: "#155724", 
                           fontSize: "1rem",
                           fontWeight: "500"
                         }}>
                           {selectedModules.mapSystem && selectedModules.callModule ? "Bundle Discount" : "You Save"}
                         </h4>
                         <div style={{ 
                           fontSize: "1.5rem", 
                           fontWeight: "700", 
                           color: "#155724",
                           marginBottom: "5px"
                         }}>
                           ${calculateTotals().yearlySavings}
                         </div>
                         <div style={{ 
                           fontSize: "0.9rem", 
                           color: "#155724" 
                         }}>
                           {selectedModules.mapSystem && selectedModules.callModule ? "bundle savings" : "per year"}
                         </div>
                       </div>
                     )}
                    </div>

                    {/* Additional Info */}
                    <div style={{ 
                      textAlign: "center", 
                      padding: "15px", 
                      backgroundColor: "white", 
                      borderRadius: "8px",
                      border: "1px solid #dee2e6"
                    }}>
                      <div style={{ 
                        fontSize: "1.1rem", 
                        fontWeight: "600", 
                        color: "#495057",
                        marginBottom: "8px"
                      }}>
                        Selected Modules: {getSelectedModulesCount()}
                      </div>
                      <div style={{ 
                        fontSize: "0.9rem", 
                        color: "#6c757d",
                        lineHeight: "1.4"
                      }}>
                        {selectedModules.mapSystem && selectedModules.callModule ? 
                          "Map System + Call Module" : 
                          selectedModules.mapSystem ? "Map System" : 
                          selectedModules.callModule ? "Call Module" : "None"
                        }
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // activeStep == 3
            <div>
              <h2 className="registerTag" style={{ marginBottom: "22px", padding: "9px" }}>
                Submit Registration
              </h2>
              <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
                Review your information and submit your registration.
              </p>

              {/* Payment method selection - Commented out as payments will be handled outside the system
              {paymentMethod !== "creditCard" && (
                <div className="register__field">
                  <button
                    className="button login__submit CreditCardButton"
                    onClick={(e) => {
                      e.preventDefault();
                      setPaymentMethod("creditCard");
                    }}
                    style={{ marginTop: "15px" }}
                  >
                    Pay with Credit Card
                  </button>
                  <button
                    disabled={payingInvoice}
                    className="button login__submit"
                    onClick={handleInvoicePayment}
                  >
                    {payingInvoice ? "Sending Invoice..." : "Submit Invoice Request"}
                  </button>
                </div>
              )}
              */}
              <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "5px" }}>
                <h4 style={{ marginBottom: "10px" }}>Registration Summary:</h4>
                <p><strong>Name:</strong> {userInfo.first} {userInfo.last}</p>
                <p><strong>Email:</strong> {userInfo.email}</p>
                <p><strong>Client:</strong> {clientInfo.clientName}</p>
                <p><strong>Selected Modules:</strong> {
                  clientInfo.plan === 1 ? "Map System" :
                  clientInfo.plan === 2 ? "Call Module" :
                  clientInfo.plan === 3 ? "Map System + Call Module" :
                  "None selected"
                }</p>
              </div>

              <button
                className="button login__submit"
                onClick={handleSubmitRegistration}
                disabled={isLoading}
                style={{ 
                  backgroundColor: "white", 
                  color: "blue",
                  marginTop: "15px",
                  width: "100%"
                }}
              >
                {isLoading ? 'Processing...' : 
                 registrationMode === 'join' ? 'Join Organization' : 'Submit Registration'}
              </button>

              {/* {paymentMethod === "creditCard" && (
                <div>
                  <StripeContainer
                    childProps={{
                      email: userInfo.email,
                      password: userInfo.password,
                      clientInfo,
                      userInfo,
                      selectedModules,
                    }}
                  />
                  <button
                    className="button login__submit CreditCardBackButton"
                    onClick={(e) => {
                      e.preventDefault();
                      setPaymentMethod(null);
                    }}
                  >
                    Choose Another method
                  </button>
                </div>
              )} */}
            </div>
          )}

          {isErrorMessage && (
            <div style={{ padding: "20px", textAlign: "center", color: "red", fontWeight: 600 }}>
              {isErrorMessage}
            </div>
          )}
          <MobileStepper
            variant="dots"
            steps={4}
            position="static"
            activeStep={activeStep}
            className="stepperUi"
            nextButton={
              <Button size="small" onClick={handleNext} disabled={activeStep === 3}>
                <b>Next</b>
                <KeyboardArrowRight className="arrowColor" />
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                <KeyboardArrowLeft className="arrowColor" />
                <b>Back</b>
              </Button>
            }
          />
        </form>
      </div>

      {/* Email Options Modal */}
      {showEmailOptionsModal && (
        <div className="choice-modal-overlay">
          <div className="choice-modal">
            <div className="choice-modal-content">
              <h2 style={{ 
                color: '#452d9f', 
                textAlign: 'center', 
                marginBottom: '20px',
                fontSize: '24px',
                fontWeight: '700'
              }}>
                Email Already Registered
              </h2>
              
              <p style={{ 
                textAlign: 'center', 
                marginBottom: '30px', 
                color: '#666',
                fontSize: '16px'
              }}>
                This email is already registered. You can either:
              </p>
              
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ 
                  color: '#452d9f', 
                  marginBottom: '15px',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Join an existing client organization:
                </h3>
                
                <select
                  value={selectedExistingClient}
                  onChange={(e) => setSelectedExistingClient(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '16px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 2px 11px #000000b0',
                    marginBottom: '20px'
                  }}
                >
                  <option value="">Select a client organization...</option>
                  {existingClients.map((client) => (
                    <option key={client.clientID} value={client.clientID}>
                      {client.clientName} {client.plan ? `(Plan ${client.plan})` : ''}
                    </option>
                  ))}
                </select>
                
                <button 
                  className="button login__submit"
                  style={{ 
                    boxShadow: '0px 2px 11px #000000b0',
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    width: '100%',
                    marginBottom: '20px'
                  }}
                  onClick={handleJoinExistingClient}
                >
                  Join Selected Organization
                </button>
              </div>
              
              <div style={{ 
                borderTop: '2px solid #eee', 
                paddingTop: '20px',
                textAlign: 'center'
              }}>
                <h3 style={{ 
                  color: '#452d9f', 
                  marginBottom: '15px',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Or create a new client organization:
                </h3>
                
                <button 
                  className="button login__submit"
                  style={{ 
                    boxShadow: '0px 2px 11px #000000b0',
                    backgroundColor: '#452d9f',
                    color: 'white',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    width: '100%',
                    marginBottom: '15px'
                  }}
                  onClick={handleCreateNewClient}
                >
                  Create New Organization
                </button>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <button 
                  className="button login__submit"
                  style={{ 
                    boxShadow: '0px 2px 11px #000000b0',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                  onClick={handleCancelEmailOptions}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;
